import React from 'react'

import { selfReferencingCanonical } from '@smarty-nx/code-utils'
import { graphql, Script, useStaticQuery } from 'gatsby'

import { CONFIG } from '../../config'

const defaultDescription =
  'SMARTY is a new SIM-only mobile network that’s built to be fair, transparent and smart.'

type HeadProps = {
  description?: string
  lang?: string
  keywords?: string[]
  title: string
  canonical?: string
  /* the page's location, as provided by Gatsby's Head API - used to build a
   * self-referencing canonical URL when `canonical` isn't explicitly set */
  location: { pathname: string }
  preventTitleTemplate?: boolean
  children?: React.ReactNode
}

const PageHead = ({
  description = defaultDescription,
  lang = 'en',
  keywords = [],
  title,
  canonical,
  location,
  preventTitleTemplate,
  children,
}: HeadProps) => {
  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          description
          author
          siteUrl
        }
      }
    }
  `)

  const resolvedCanonical =
    canonical ??
    selfReferencingCanonical(site.siteMetadata.siteUrl, location.pathname)

  const meta = [
    {
      name: 'description',
      content: description,
    },
    {
      property: 'og:title',
      content: title,
    },
    {
      property: 'og:description',
      content: description,
    },
    {
      property: 'og:image',
      content: '/assets/images/smarty_logo.png',
    },
    {
      property: 'og:type',
      content: 'website',
    },
    {
      name: 'twitter:card',
      content: 'summary',
    },
    {
      name: 'twitter:creator',
      content: site.siteMetadata.author,
    },
    {
      name: 'twitter:title',
      content: title,
    },
    {
      name: 'twitter:description',
      content: description,
    },
    {
      name: 'twitter:image',
      content: '/assets/images/smarty_logo.png',
    },
    {
      'http-equiv': 'x-ua-compatible',
      content: 'ie-edge',
    },
    {
      name: 'viewport',
      content: 'width=device-width, initial-scale=1, shrink-to-fit=no',
    },
  ].concat(
    keywords.length > 0
      ? {
          name: 'keywords',
          content: keywords.join(', '),
        }
      : []
  )

  return (
    <>
      <html lang={lang} />
      <title>
        {preventTitleTemplate ? title : `${title} | ${site.siteMetadata.title}`}
      </title>
      <link
        rel="shortcut icon"
        sizes="16x16 32x32"
        type="image/png"
        href={`${CONFIG.MARKETING_URL}favicon.png`}
      />
      <link
        rel="apple-touch-icon"
        href={`${CONFIG.MARKETING_URL}apple-touch-icon.png`}
      />
      <link rel="canonical" href={resolvedCanonical} />
      <link
        rel="sitemap"
        type="application/xml"
        href={`${CONFIG.MARKETING_URL}sitemap.xml`}
      />
      <Script
        src="https://www.dwin1.com/10933.js"
        type="text/javascript"
        defer
      />
      {meta.map((item, index) => (
        <meta
          key={index}
          name={item.name}
          property={item.property}
          content={item.content}
          httpEquiv={item['http-equiv']}
        />
      ))}
      {children}
    </>
  )
}

export default PageHead
